import { MenuItem, Text } from "@chakra-ui/react";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { maybePluralize } from "src/services/format";
import { FormTemplateSettings } from "src/types/formTemplate/v2";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canDeclineOffers } from "../validations/declineOffers";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkOffer } from "./useEnqueueBulkOffer";

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Declining this offer will prevent or undo the family's acceptance.",
    "Declining these offers will prevent or undo the families' acceptance."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, decline offer", "Yes, decline offers");

interface Props {
  formTemplateSettings: Pick<FormTemplateSettings, "allowDeclineAcceptedOffer">;
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
}

export const BulkDeclineOffer = ({
  formTemplateSettings,
  selectedFormSchoolStatuses,
  selectedFormSchools,
}: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkOffer = useEnqueueBulkOffer();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "DECLINE_OFFER",
      bulk: true,
      count,
      async: true,
    });

    if (
      !(await confirm({
        header: (
          <Text>{`Decline ${count} ${maybePluralize(count, "offer")}?`}</Text>
        ),
        body: <Text>{confirmationBodyText(count)}</Text>,
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      }))
    ) {
      trackFormAction({
        verb: "Canceled",
        actionType: "DECLINE_OFFER",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "DECLINE_OFFER",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkOffer({ actionType: "DECLINE_OFFER" });
  };

  return (
    <WithUserPermissions permissions={["form:update"]}>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canDeclineOffers(
            selectedFormSchoolStatuses,
            selectedFormSchools,
            ["both", "admin"].includes(
              formTemplateSettings.allowDeclineAcceptedOffer
            )
          )
        }
      >
        Decline offers
      </MenuItem>
      {confirmationDialog}
    </WithUserPermissions>
  );
};
